import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { newId, unitByCode } from '@gk/contracts'
import { previewBase, unitsFor, unitSymbol, useBatches, useLatestVersions, useProducts, useStockLocations, useWorkOrderParts, type ProductRow, type WorkOrderPartRow } from '../data/catalog.ts'
import { filterProducts } from '../data/catalogueUi.ts'
import { useAssets } from '../data/fleet.ts'
import { useWorkOrders } from '../data/safety.ts'
import { locationLabel, siteLocations } from '../data/stockUi.ts'
import { recentProductIds } from '../data/workshopUi.ts'
import { useClub } from '../club/ClubProvider.tsx'
import { fmtNum } from '../lib/format.ts'
import { Banner, DisabledHint, errorText, Field, PendingBanner, Pill } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import { TwoTap, useCloseOnPending } from './workshop/shared.tsx'

/** How many product rows the picker shows at once (M23 item 36): the recent ones, or the search's matches; the whole list stays one select away. */
export const PICKER_ROWS = 6

/** The parts used on a work order (GK-024). Each line is kept as entered AND in the product's own unit, converted by the server under the product's newest version (its density, when the
 *  entry is a mass for a volume or the reverse). The device shows a preview with the units twin and says why when it would be refused; the server decides. Online-first, like the rest of the workshop. */
export function WorkOrderParts({ orderId, assetId, open }: { orderId: string; assetId: string; open: boolean }) {
  const { t, i18n } = useTranslation()
  const manage = useWriteAccess('workorder.manage')
  const parts = useWorkOrderParts(); const products = useProducts(); const batches = useBatches()
  const lang = i18n.language
  const mine = useMemo(() => (parts ?? []).filter((p) => p.work_order_id === orderId).sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? '') || a.id.localeCompare(b.id)), [parts, orderId])
  const live = mine.filter((p) => !p.removed_at)
  const gone = mine.length - live.length
  const productName = new Map((products ?? []).map((p) => [p.id, p.name]))
  const lotOf = new Map((batches ?? []).map((b) => [b.id, b.lot]))
  if (!parts || !products || !batches) return null
  if (live.length === 0 && !(open && manage.allowed)) return null
  return (
    <div className="flex flex-col gap-2 border-t pt-2" data-testid="order-parts">
      <h4 className="text-sm font-bold">{t('parts.title')}</h4>
      {live.length === 0 ? <p className="meta" data-testid="parts-none">{t('parts.none')}</p> : (
        <ul className="flex flex-col gap-1">
          {live.map((p) => <PartLine key={p.id} part={p} name={productName.get(p.product_id) ?? '?'} lot={p.batch_id ? lotOf.get(p.batch_id) ?? null : null} lang={lang} canRemove={open && manage.allowed} />)}
        </ul>
      )}
      {gone > 0 && <p className="meta" data-testid="parts-removed">{t('parts.removed', { count: gone })}</p>}
      {open && manage.allowed && <AddPart orderId={orderId} assetId={assetId} />}
    </div>
  )
}

function PartLine({ part, name, lot, lang, canRemove }: { part: Pending<WorkOrderPartRow>; name: string; lot: string | null; lang: string; canRemove: boolean }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const entered = `${fmtNum(Number(part.qty_entered), lang, 3)} ${unitSymbol(part.unit_entered, lang)}`
  const base = `${fmtNum(Number(part.qty_base), lang, 3)} ${unitSymbol(part.base_unit_code, lang)}`
  return (
    <li className="flex flex-col gap-1" data-testid="part-row" data-part={part.id}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0"><span className="font-semibold">{name}</span> · <span data-testid="part-qty">{entered}</span>{part.unit_entered !== part.base_unit_code && <span className="opacity-70" data-testid="part-base"> (= {base})</span>}
          {lot && <span className="opacity-70"> · {t('parts.lot', { lot })}</span>}{part._pending && <> <Pill tone="amber">{t('common.pendingSend')}</Pill></>}</span>
        {canRemove && !part._pending && (
          <TwoTap label={t('parts.remove')} confirm={t('parts.removeConfirm')} danger disabled={w.busy} testId="part-remove" className="!min-h-11 !px-3 text-sm"
            onConfirm={() => void w.run('workorder.remove_part@2', { part_id: part.id }, 'parts.removedDone')} />   /* M15-A: @2 puts the part back in the stock */
        )}
      </div>
      {part.note && <p className="meta">{part.note}</p>}
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error">{w.error}</Banner>}
    </li>
  )
}

/** Adding a part (M23 item 36): a search and big rows instead of a native dropdown, the products most recently used on this type of machine first;
 *  the whole catalogue stays one select away. The quantity and its unit are asked as soon as a product is picked. */
function AddPart({ orderId, assetId }: { orderId: string; assetId: string }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { site } = useClub()
  const products = useProducts(); const batches = useBatches(); const latest = useLatestVersions()
  const parts = useWorkOrderParts(); const orders = useWorkOrders(); const assets = useAssets()
  // M15-A: the place the part is taken from (add_part@2 writes the consumption with the line; the site's default unless another is picked)
  const locations = useStockLocations()
  const places = useMemo(() => siteLocations(locations ?? [], site.site_id), [locations, site.site_id])
  const [locationId, setLocationId] = useState('')
  const chosenPlace = places.some((l) => l.id === locationId) ? locationId : places.find((l) => l.is_default)?.id ?? places[0]?.id ?? ''
  const w = useWorkshopAction()
  const id = useRef(newId())
  const [show, setShow] = useState(false)
  const [query, setQuery] = useState('')
  const [productId, setProductId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [unit, setUnit] = useState('')
  const [batchId, setBatchId] = useState('')
  const [note, setNote] = useState('')
  const usable = useMemo(() => (products ?? []).filter((p) => !p.archived_at).sort((a, b) => a.name.localeCompare(b.name, lang)), [products, lang])
  const recent = useMemo(() => recentProductIds(parts ?? [], orders ?? [], assets ?? [], assetId), [parts, orders, assets, assetId])
  const matches = useMemo(() => filterProducts(usable, { query, kind: 'all', showArchived: false }), [usable, query])
  const rows: ProductRow[] = query.trim() ? matches.slice(0, PICKER_ROWS) : recent.map((pid) => usable.find((p) => p.id === pid)).filter((p): p is Pending<ProductRow> => !!p)
  const product = usable.find((p) => p.id === productId)
  const version = product ? latest?.get(product.id) : undefined
  const units = product ? unitsFor(product.base_unit_code, version?.density_kg_per_l != null) : []
  const chosenUnit = units.some((u) => u.code === unit) ? unit : product?.base_unit_code ?? ''
  const lots = (batches ?? []).filter((b) => b.product_id === productId)
  const preview = product && quantity.trim() ? previewBase(quantity, chosenUnit, product, version) : null
  const pick = (pid: string) => { setProductId(pid); setUnit(''); setBatchId('') }
  useCloseOnPending(w.pending, () => setShow(false))
  if (!show) {
    return (
      <div className="flex flex-col gap-2">
        {w.ok && <Banner tone="ok">{w.ok}</Banner>}
        {w.pending && <PendingBanner />}
        <button className="btn !min-h-11" onClick={() => { w.reset(); setShow(true) }} data-testid="part-new">{t('parts.add')}</button>
      </div>
    )
  }
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!product) return
    const q = Number(quantity.trim().replace(',', '.'))
    void w.run('workorder.add_part@2', { part_id: id.current, work_order_id: orderId, product_id: product.id, quantity: q, unit: chosenUnit, ...(batchId ? { batch_id: batchId } : {}), ...(note.trim() ? { note: note.trim() } : {}), ...(chosenPlace ? { location_id: chosenPlace } : {}) }, 'parts.added')
      .then((ok) => { if (ok) { id.current = newId(); setQuantity(''); setNote(''); setBatchId(''); setShow(false) } })
  }
  return (
    <form className="card flex flex-col gap-3" onSubmit={submit} data-testid="part-form">
      {products && usable.length === 0 && <Banner tone="info">{t('parts.noProducts')}</Banner>}
      <Field label={t('parts.search')} htmlFor={`ps-${orderId}`}>
        <input id={`ps-${orderId}`} type="search" className="input" autoComplete="off" placeholder={t('parts.searchPlaceholder')} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="part-search" />
      </Field>
      {rows.length > 0 && (
        <div className="flex flex-col gap-2" role="radiogroup" aria-label={t('parts.product')} data-testid="part-picker">
          <p className="meta">{query.trim() ? t('parts.matches') : t('parts.recent')}</p>
          {rows.map((p) => (
            <button key={p.id} type="button" role="radio" aria-checked={productId === p.id} className={`btn w-full justify-between ${productId === p.id ? 'btn-primary' : ''}`} onClick={() => pick(p.id)} data-testid="part-pick" data-product={p.id}>
              <span className="min-w-0 truncate text-left">{p.name}</span><span className={`shrink-0 text-sm ${productId === p.id ? '' : 'opacity-80'}`}>{unitSymbol(p.base_unit_code, lang)}</span>
            </button>
          ))}
          {query.trim() && matches.length > PICKER_ROWS && <p className="meta">{t('parts.more', { count: matches.length - PICKER_ROWS })}</p>}
        </div>
      )}
      {query.trim() && matches.length === 0 && <p className="meta" data-testid="part-no-match">{t('parts.noMatch')}</p>}
      <Field label={t('parts.allList')} htmlFor={`pp-${orderId}`}>
        <select id={`pp-${orderId}`} className="input" value={productId} onChange={(e) => pick(e.target.value)} data-testid="part-product">
          <option value="">{t('parts.choose')}</option>
          {usable.map((p) => <option key={p.id} value={p.id}>{p.name} ({unitSymbol(p.base_unit_code, lang)})</option>)}
        </select>
      </Field>
      {product && (
        <>
          <p className="font-semibold" data-testid="part-chosen">{product.name}</p>
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <Field label={t('parts.quantity')} htmlFor={`pq-${orderId}`}>
              <input id={`pq-${orderId}`} className="input" inputMode="decimal" autoComplete="off" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="part-quantity" />
            </Field>
            <Field label={t('parts.unit')} htmlFor={`pu-${orderId}`}>
              <select id={`pu-${orderId}`} className="input" value={chosenUnit} onChange={(e) => setUnit(e.target.value)} data-testid="part-unit">
                {units.map((u) => <option key={u.code} value={u.code}>{unitSymbol(u.code, lang)}</option>)}
              </select>
            </Field>
          </div>
          {version?.density_kg_per_l == null && (unitByCode(product.base_unit_code)?.dimension === 'mass' || unitByCode(product.base_unit_code)?.dimension === 'volume') && <p className="meta" data-testid="part-density-hint">{t('parts.noDensityHint')}</p>}
          {preview && (preview.ok
            ? (chosenUnit !== product.base_unit_code && <p className="text-sm font-semibold" data-testid="part-preview">= {fmtNum(Number(preview.qtyBase), lang, 3)} {unitSymbol(product.base_unit_code, lang)}</p>)
            : <p role="alert" className="text-sm font-medium text-red-700 dark:text-red-300" data-testid="part-preview-error">{preview.code === 'QUANTITY_INVALID' ? t('parts.quantityInvalid') : errorText(t, preview.code)}</p>)}
          {lots.length > 0 && (
            <Field label={t('parts.batch')} htmlFor={`pb-${orderId}`}>
              <select id={`pb-${orderId}`} className="input" value={batchId} onChange={(e) => setBatchId(e.target.value)} data-testid="part-batch">
                <option value="">{t('parts.noBatch')}</option>
                {lots.map((b) => <option key={b.id} value={b.id}>{b.lot}</option>)}
              </select>
            </Field>
          )}
          <Field label={t('parts.note')} htmlFor={`pn-${orderId}`}><input id={`pn-${orderId}`} className="input" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} data-testid="part-note" /></Field>
          {places.length > 1
            ? <Field label={t('stock.parts.location')} hint={t('stock.parts.locationHint')} htmlFor={`pl-${orderId}`}>
                <select id={`pl-${orderId}`} className="input" value={chosenPlace} onChange={(e) => setLocationId(e.target.value)} data-testid="part-location">
                  {places.map((l) => <option key={l.id} value={l.id}>{locationLabel(l, lang)}</option>)}
                </select>
              </Field>
            : places[0] && <p className="meta" data-testid="part-location-text">{t('stock.parts.takenFrom', { location: locationLabel(places[0], lang) })}</p>}
        </>
      )}
      {w.error && <Banner tone="error"><span data-testid="part-error">{w.error}</span></Banner>}
      <DisabledHint show={!product || !quantity.trim()} testId="part-save-hint">{t('parts.saveHint')}</DisabledHint>
      <div className="flex gap-2">
        <button className="btn btn-primary flex-1" disabled={w.busy || !product || !quantity.trim() || (preview !== null && !preview.ok)} data-testid="part-save">{w.busy ? t('common.saving') : t('parts.save')}</button>
        <button type="button" className="btn" onClick={() => setShow(false)}>{t('common.cancel')}</button>
      </div>
    </form>
  )
}
